<script setup lang="ts">
import { CategoryType, type SearchSuggestions } from "mws-ts-v2-sdk";

import { SEARCH_INPUT_ID } from "@/constants/misc";

const { isSearchInputActive, closeSearchModal } = useSearchBar();
const { addSearchHistory } = useSearchHistory();
const router = useRouter();
const route = useRoute();
const localePath = useLocalePath();
const searchModalRef = useTemplateRef("searchModalRef");

const searchTerm = ref("");

const routeSearchTerm = computed(() =>
  transformRouteParams(route.params.searchTerm, "string"),
);

// If there is a search term in the route params and nothing has been typed,
// then prefill the search term
watch(
  routeSearchTerm,
  () => {
    if (!searchTerm.value && routeSearchTerm.value)
      searchTerm.value = routeSearchTerm.value;
  },
  { immediate: true },
);

const onCloseSearchModal = () => {
  closeSearchModal();
  searchTerm.value = "";
};

const isUserEnteredIntoSearchExperiment = computed(
  () => isSearchInputActive.value,
);

const { result } = useGrowthbook({
  feature: "search-category-navigation-web",
  fallback: false,
  enabled: isUserEnteredIntoSearchExperiment,
});

const onEnterSearchResult = async (
  search?: SearchHistoryItem,
  exactMatchSuggestion?: SearchSuggestions | null,
) => {
  const searchName = getSearchHistoryName(search || "");
  if (!search || searchName.trim().length < 2) return;

  const searchObject =
    exactMatchSuggestion || (typeof search === "object" ? search : null);

  if (
    result.value &&
    searchObject &&
    (searchObject.type === CategoryType.Campaign ||
      searchObject.type === CategoryType.Team)
  ) {
    addSearchHistory(searchObject);

    await router.push(
      localePath({
        path: `/category/${searchObject.slug}`,
      }),
    );
  } else {
    addSearchHistory(search);

    await router.push(
      localePath({
        path: `/search/${searchName}`,
      }),
    );
  }

  closeSearchModal();
};

watch(isSearchInputActive, (isActive) => {
  if (isActive) {
    nextTick(() => {
      const searchInput = document.getElementById(SEARCH_INPUT_ID);

      if (searchInput) searchInput.focus();
    });
  }
});
</script>

<template>
  <BaseDialog
    v-model="isSearchInputActive"
    class="w-full"
    :close-button="false"
  >
    <LazySearchBarDesktop
      v-model:search-term="searchTerm"
      @search="
        () =>
          onEnterSearchResult(searchTerm, searchModalRef?.exactMatchSuggestion)
      "
      @close-search-bar="onCloseSearchModal"
    >
      <SearchModal
        ref="searchModalRef"
        :is-mobile="false"
        :search-term="searchTerm || ''"
        @enter-search-result="onEnterSearchResult"
        @close-search-bar="onCloseSearchModal"
      />
    </LazySearchBarDesktop>

    <LazySearchBarMobile
      v-model:search-term="searchTerm"
      @search="
        () =>
          onEnterSearchResult(searchTerm, searchModalRef?.exactMatchSuggestion)
      "
      @close-search-bar="onCloseSearchModal"
    >
      <SearchModal
        ref="searchModalRef"
        :is-mobile="true"
        class="min-h-[calc(100vh-64px)] px-4 pt-6 pb-4"
        :grid-amount="2"
        :search-term="searchTerm || ''"
        @enter-search-result="onEnterSearchResult"
        @close-search-bar="onCloseSearchModal"
      />
    </LazySearchBarMobile>
  </BaseDialog>
</template>
